import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import './Spiritualite.css';

const PILIERS = [
  {
    num: "I",
    glyph: "ﷲ",
    title: "Tawhîd",
    translit: "L'Unicité",
    desc: "Reconnaissance de l'unicité divine — fondement absolu de la foi musulmane, vécu comme une présence constante dans le quotidien.",
  },
  {
    num: "II",
    glyph: "✦",
    title: "Pureté",
    translit: "Tahâra",
    desc: "Le blanc immaculé, les ablutions à l'aube, la propreté intérieure et extérieure — la pureté du corps comme préparation de l'âme.",
  },
  {
    num: "III",
    glyph: "◯",
    title: "Communauté",
    translit: "Umma",
    desc: "L'islam Layène se vit collectivement — la fraternité comme prolongement de la foi, la solidarité comme acte de prière.",
  },
  {
    num: "IV",
    glyph: "♪",
    title: "Transmission",
    translit: "Tassou",
    desc: "Les chants de dévotion en wolof transmettent le savoir spirituel de génération en génération. La voix porte la mémoire.",
  },
];

const SEMAINE = [
  { day: "Lundi",    title: "Étude",       desc: "Cercles de lecture du Coran et des écrits de Seydina Limamou Laye, dans les mosquées de quartier." },
  { day: "Mardi",    title: "Silence",     desc: "Journée de retrait personnel — méditation, lectures privées, prières surérogatoires." },
  { day: "Mercredi", title: "Tassou",      desc: "Soirée de chants collectifs — les voix des femmes portent le tassou à travers les ruelles.", major: true },
  { day: "Jeudi",    title: "Préparation", desc: "Lessive du blanc, repassage, préparation des tenues pour le vendredi — un rituel domestique." },
  { day: "Vendredi", title: "Jumu'a",      desc: "Prière collective à la grande mosquée Layène — moment central de la semaine.", major: true },
  { day: "Samedi",   title: "Visites",     desc: "Visite aux aînés, aux familles endeuillées, aux malades — la communauté en mouvement." },
  { day: "Dimanche", title: "Ablutions",   desc: "Ablutions communautaires au bord de mer à l'aube — purification face à l'océan." },
];

const CELEBRATIONS = [
  {
    date: "Mai · annuel",
    title: "Niani · Le Grand Appel",
    desc: "Commémoration de la révélation du Mahdi (1884). Des milliers de fidèles convergent à Cambérène pour trois jours de chants, processions et prières.",
    seed: "cb-spi-niani-procession",
  },
  {
    date: "Rabi' al-Awwal",
    title: "Mawlid",
    desc: "Anniversaire de la naissance du Prophète Muhammad — veillées de chants, illumination des mosquées, distribution de bénédictions.",
    seed: "cb-spi-mawlid",
  },
  {
    date: "Ramadan",
    title: "Le mois sacré",
    desc: "Jeûne, prières nocturnes (Taraweeh), partage des repas de rupture — Cambérène vit au rythme spirituel intensifié du mois saint.",
    seed: "cb-spi-ramadan",
  },
  {
    date: "Tabaski",
    title: "Aïd el-Kebir",
    desc: "Grande fête du sacrifice — partage de la viande avec les voisins, les pauvres et les familles éloignées. La communauté à son paroxysme.",
    seed: "cb-spi-tabaski",
  },
];

const SYMBOLES = [
  {
    lbl: "Vêtement",
    title: "Le blanc immaculé",
    desc: "Porté quotidiennement par les fidèles, le blanc symbolise la pureté de l'âme et l'égalité devant Dieu — pas de hiérarchie visible dans la couleur.",
    seed: "cb-spi-sym-blanc",
  },
  {
    lbl: "Eau",
    title: "L'aube et l'océan",
    desc: "Les ablutions à la mer avant la prière du Fajr — l'Atlantique devient eau sacrée, et Cambérène, géographie spirituelle.",
    seed: "cb-spi-sym-ocean",
  },
  {
    lbl: "Voix",
    title: "Le chant tassou",
    desc: "Poésie dévotionnelle wolof, transmise par les femmes — porteuse de l'enseignement Layène quand peu savaient lire l'arabe.",
    seed: "cb-spi-sym-tassou",
  },
  {
    lbl: "Lieu",
    title: "La mosquée face mer",
    desc: "Orientée vers La Mecque mais ouverte sur l'océan — symbole du pont entre le monde matériel et le monde spirituel.",
    seed: "cb-spi-sym-mosquee",
  },
];

const Spiritualite = () => {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.spi-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  }, []);

  return (
    <main className="spi-page">
      {/* ============== HERO ============== */}
      <header className="spi-hero">
        <div className="spi-hero-bg" />
        <div className="spi-hero-grain" />
        <div className="spi-hero-arabic" aria-hidden="true">Bismillāh</div>

        <div className="wrap spi-hero-inner reveal">
          <span className="spi-hero-eyebrow">Tariqa Layène · Cambérène · Depuis 1884</span>
          <h1>
            Foi, blanc,<br />
            <span className="ital">tassou.</span>
          </h1>
          <p className="lede">
            Une voie soufie née à Cambérène en 1884 sous l'impulsion de Seydina Limamou Laye —
            une spiritualité de l'amour, de la pureté et du chant, où le quotidien devient prière
            et l'océan, lieu d'ablution.
          </p>

          <div className="spi-hero-foot">
            <div><b>1884</b><span>Révélation du Mahdi</span></div>
            <div><b>4</b><span>Piliers fondateurs</span></div>
            <div><b>50&nbsp;000+</b><span>Adeptes au Sénégal</span></div>
            <div><b>1</b><span>Voie unique</span></div>
          </div>
        </div>
      </header>

      {/* ============== MANIFESTE ============== */}
      <section className="spi-manifeste reveal">
        <div className="wrap">
          <p>
            « La voie Layène enseigne que la foi se vit dans la beauté du quotidien —
            la blancheur du vêtement, la pureté du chant, la discipline du corps et l'amour du frère. »
          </p>
          <span className="src">Archives de Cambérène · Recueil des enseignements, 2024</span>
        </div>
      </section>

      {/* ============== PILIERS ============== */}
      <section className="spi-section spi-piliers">
        <div className="wrap">
          <div className="spi-head reveal">
            <div>
              <span className="eyebrow">Quatre piliers</span>
              <h2>Les fondements<br /><span className="ital">de la voie.</span></h2>
            </div>
            <p>
              Une lecture soufie de l'islam, centrée sur l'amour divin et la pratique
              communautaire. Quatre principes qui guident la vie spirituelle layène depuis sept générations.
            </p>
          </div>

          <div className="pil-grid">
            {PILIERS.map((p) => (
              <article key={p.title} className="pil-card reveal">
                <span className="pil-num">{p.num}</span>
                <span className="pil-glyph">{p.glyph}</span>
                <h4>{p.title}</h4>
                <span className="pil-translit">{p.translit}</span>
                <p>{p.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== RITUEL HEBDOMADAIRE ============== */}
      <section className="spi-section spi-rituel">
        <div className="wrap">
          <div className="spi-head reveal">
            <div>
              <span className="eyebrow">Semaine spirituelle</span>
              <h2>Le rythme<br /><span className="ital">d'une semaine.</span></h2>
            </div>
            <p>
              À Cambérène, la spiritualité n'est pas réservée au vendredi — elle s'inscrit
              dans chaque jour de la semaine, dans des rites publics ou intimes.
            </p>
          </div>

          <div className="rit-grid">
            {SEMAINE.map((d) => (
              <article key={d.day} className={`rit-day reveal${d.major ? ' major' : ''}`}>
                <span className="d-name">{d.day}</span>
                <h5>{d.title}</h5>
                <p>{d.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CÉLÉBRATIONS ============== */}
      <section className="spi-section spi-celeb">
        <div className="wrap">
          <div className="spi-head reveal">
            <div>
              <span className="eyebrow">Calendrier annuel</span>
              <h2>Quatre grands<br /><span className="ital">rendez-vous.</span></h2>
            </div>
            <p>
              Les célébrations qui scandent l'année spirituelle à Cambérène — moments où
              la communauté se rassemble, se dépasse et se transmet.
            </p>
          </div>

          <div className="cel-grid">
            {CELEBRATIONS.map((c) => (
              <article key={c.title} className="cel-card reveal">
                <div className="img" style={{ backgroundImage: `url('https://picsum.photos/seed/${c.seed}/1200/1500')` }} />
                <div className="grad" />
                <div className="body">
                  <span className="date">{c.date}</span>
                  <h4>{c.title}</h4>
                  <p>{c.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SYMBOLES ============== */}
      <section className="spi-section spi-symbols">
        <div className="wrap">
          <div className="spi-head reveal">
            <div>
              <span className="eyebrow">Décoder les signes</span>
              <h2>Quatre symboles,<br /><span className="ital">une voie.</span></h2>
            </div>
            <p>
              Le tissu blanc, l'eau de l'aube, la voix qui chante, la mosquée face océan —
              quatre éléments qui rendent visible une spiritualité tout intérieure.
            </p>
          </div>

          <div className="sym-grid">
            {SYMBOLES.map((s) => (
              <div key={s.title} className="sym-row reveal">
                <div
                  className="sym-photo"
                  style={{ backgroundImage: `url('https://picsum.photos/seed/${s.seed}/600/600')` }}
                />
                <div className="sym-content">
                  <span className="sym-lbl">{s.lbl}</span>
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CTA ============== */}
      <section className="spi-cta">
        <div className="wrap reveal">
          <span className="eyebrow">Approfondir</span>
          <h3>Écouter, lire, comprendre <span className="ital">la voie Layène.</span></h3>
          <p>
            Enregistrements de tassou, textes fondateurs traduits, témoignages d'aînés
            et programme du prochain Grand Appel — tout est accessible.
          </p>
          <div className="spi-cta-actions">
            <Link to="/programme-niane" className="btn btn-primary">
              <span>Programme du Niani</span>
              <span className="arrow">→</span>
            </Link>
            <Link to="/bibliotheque" className="btn btn-outline">
              <span>Bibliothèque numérique</span>
              <span className="arrow">↗</span>
            </Link>
            <Link to="/histoire" className="btn btn-outline">
              <span>Histoire du village</span>
              <span className="arrow">↗</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Spiritualite;
